/**
 * PrLitReviewStatusChart
 *
 * A self-contained status breakdown chart for one or more PR literature review
 * collections. It renders:
 *   - a shared colour legend,
 *   - one horizontal stacked bar per collection (label, uploaded/total counts,
 *     and the stacked segments), and
 *   - a single hover tooltip (portalled to document.body) that follows the
 *     cursor when a segment is hovered.
 *
 * Callers pass the raw per-collection status counts (from the
 * `getJournalArticleStatusCounts` endpoint). The chart internally computes bar
 * widths relative to the largest collection in the set, so passing a single
 * collection produces a full-width bar.
 *
 * Used by PrLitReviewOverviewPage (many collections) and
 * PrLitReviewCollectionPage (one collection).
 */

import { useMemo, useState } from "react"
import { createPortal } from "react-dom"
import { scaleLinear } from "d3"
import {
  StatusCounts,
  JournalArticleStatusCountsResponse,
  extractionStatusLabelMap,
} from "@/services/journalArticlesService"

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

/**
 * The status categories, in the order they stack (left to right) and appear in
 * the legend. These are the keys of `StatusCounts` and match the categories the
 * backend `status-counts` endpoint returns.
 */
const CATEGORY_ORDER: readonly (keyof StatusCounts)[] = [
  "UPLOADED",
  "EXTRACTED",
  "WAITING_FOR_AUTHOR",
  "IN_PROGRESS",
  "NOT_STARTED",
  "EXCLUDED",
] as const

/**
 * Maps a category to a fill colour. Extraction-status colours match the
 * journal articles table (`ExtractStatusBadge`); the two derived categories
 * (Uploaded, Waiting for Author) use distinct colours.
 */
const CATEGORY_FILL_MAP: Record<keyof StatusCounts, string> = {
  NOT_STARTED: "#f87171", // red-400
  IN_PROGRESS: "#60a5fa", // blue-400
  EXTRACTED: "#4ade80", // green-400
  EXCLUDED: "#9ca3af", // gray-400
  UPLOADED: "#059669", // emerald-600 (a deeper "done" green)
  WAITING_FOR_AUTHOR: "#a78bfa", // violet-400
}

/**
 * Display labels for the derived categories. Raw extraction statuses fall back
 * to `extractionStatusLabelMap`.
 */
const DERIVED_CATEGORY_LABELS: Record<string, string> = {
  UPLOADED: "Uploaded",
  WAITING_FOR_AUTHOR: "Waiting for Author",
}

/** Zero-filled counts, used when a collection has no fetched counts entry. */
const EMPTY_STATUS_COUNTS: StatusCounts = {
  UPLOADED: 0,
  EXTRACTED: 0,
  WAITING_FOR_AUTHOR: 0,
  IN_PROGRESS: 0,
  NOT_STARTED: 0,
  EXCLUDED: 0,
}

/** Height of each collection's horizontal bar, in pixels. */
const BAR_HEIGHT: number = 20

// ---------------------------------------------------------------------------
// Internal types
// ---------------------------------------------------------------------------

/** A single category segment within a collection's bar. */
interface RowSegment {
  /** The status category this segment represents. */
  category: keyof StatusCounts
  /** The number of articles in this category. */
  count: number
  /** The fill colour for this segment. */
  fill: string
}

/** A collection's computed row: its label, totals, and category segments. */
interface CollectionRow {
  /** The collection's id (used as the React key). */
  collectionId: number
  /** The collection's display name. */
  label: string
  /** Total number of articles in the collection. */
  total: number
  /** Number of articles in the "Uploaded" category. */
  uploadedCount: number
  /** Number of articles not in the "Excluded" category (total minus excluded). */
  notExcludedCount: number
  /**
   * Fractional width of this bar (0–1) relative to the largest collection, so
   * bar length reflects absolute article volume across rows.
   */
  widthFraction: number
  /** The category segments that make up this bar. */
  segments: RowSegment[]
}

/**
 * Hover tooltip state: the content to show and where to position it. `left`
 * and `top` are viewport coordinates (pixels) for fixed positioning.
 */
interface TooltipState {
  /** The collection name the hovered segment belongs to. */
  collectionLabel: string
  /** The hovered segment's category label. */
  categoryLabel: string
  /** The hovered segment's article count. */
  count: number
  /** The segment's fill colour, for the tooltip swatch. */
  fill: string
  left: number
  top: number
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/**
 * Returns the human-readable label for a category (derived or raw extraction
 * status).
 *
 * @param category - The category key.
 * @returns The display label, falling back to the extraction-status label map,
 *   then to the raw key if unmapped.
 */
function getCategoryLabel(category: keyof StatusCounts): string {
  return DERIVED_CATEGORY_LABELS[category] ?? extractionStatusLabelMap[category] ?? category
}

// ---------------------------------------------------------------------------
// Public types + props
// ---------------------------------------------------------------------------

/**
 * One collection's identity and its fetched status counts, passed by the
 * caller. The chart computes bar widths, segments, and totals internally.
 */
export interface CollectionStatusEntry {
  /** The collection's numeric id. */
  collectionId: number
  /** The collection's display name. */
  collectionName: string
  /** The per-status counts fetched from the API, or undefined if not yet loaded. */
  statusCounts?: JournalArticleStatusCountsResponse
}

/** Props for {@link PrLitReviewStatusChart}. */
interface PrLitReviewStatusChartProps {
  /**
   * The collections to chart, one bar each. Pass a single entry for a
   * per-collection page, or many for the overview. The order of the array
   * determines the display order.
   */
  entries: CollectionStatusEntry[]
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

/**
 * Render the PR literature review status chart: legend, one stacked bar per
 * collection, and a shared hover tooltip.
 *
 * @param props - The chart props.
 * @returns The rendered chart element.
 */
export default function PrLitReviewStatusChart({
  entries,
}: PrLitReviewStatusChartProps): React.JSX.Element {
  // The currently hovered segment's tooltip, or null when nothing is hovered.
  const [tooltip, setTooltip] = useState<TooltipState | null>(null)

  // Compute each collection's row, including bar widths relative to the largest
  // collection. Recomputed only when entries change.
  const rows: CollectionRow[] = useMemo(() => {
    const totals: number[] = entries.map(
      (entry) => entry.statusCounts?.total ?? 0
    )
    const maxTotal: number = totals.length > 0 ? Math.max(...totals, 0) : 0
    const widthScale = scaleLinear()
      .domain([0, maxTotal > 0 ? maxTotal : 1])
      .range([0, 1])

    return entries.map((entry) => {
      const counts: StatusCounts =
        entry.statusCounts?.statusCounts ?? EMPTY_STATUS_COUNTS
      const total: number = entry.statusCounts?.total ?? 0

      const segments: RowSegment[] = CATEGORY_ORDER.map((category) => ({
        category,
        count: counts[category],
        fill: CATEGORY_FILL_MAP[category],
      }))

      return {
        collectionId: entry.collectionId,
        label: entry.collectionName,
        total,
        uploadedCount: counts.UPLOADED,
        notExcludedCount: total - counts.EXCLUDED,
        widthFraction: widthScale(total),
        segments,
      }
    })
  }, [entries])

  /**
   * Updates the tooltip from a pointer event over a segment.
   *
   * @param event - The mouse event on the segment element.
   * @param row - The collection row the segment belongs to.
   * @param segment - The hovered segment.
   */
  function handleSegmentHover(
    event: React.MouseEvent<HTMLDivElement>,
    row: CollectionRow,
    segment: RowSegment
  ): void {
    setTooltip({
      collectionLabel: row.label,
      categoryLabel: getCategoryLabel(segment.category),
      count: segment.count,
      fill: segment.fill,
      left: event.clientX,
      top: event.clientY,
    })
  }

  // Whether to show each row's collection name. When only one collection is
  // charted, the label is redundant (the page title already identifies it).
  const showCollectionLabels: boolean = rows.length > 1

  return (
    <div className="relative flex flex-col gap-4" data-chart-container>
      {/* Shared colour legend. */}
      <ul className="flex flex-wrap gap-x-4 gap-y-1.5 text-xs" aria-label="Status legend">
        {CATEGORY_ORDER.map((category) => (
          <li key={category} className="flex items-center gap-1.5">
            <span
              className="inline-block size-3 shrink-0 rounded-sm"
              style={{ backgroundColor: CATEGORY_FILL_MAP[category] }}
              aria-hidden="true"
            />
            <span className="text-muted-foreground">{getCategoryLabel(category)}</span>
          </li>
        ))}
      </ul>

      {/* Bars: one horizontal stacked bar per collection. */}
      <ul className="flex flex-col gap-2" aria-label="PR article status by collection">
        {rows.map((row) => (
          <li key={row.collectionId} className="flex flex-col gap-1">
            {showCollectionLabels && (
              <span className="truncate text-sm font-medium" title={row.label}>
                {row.label}
              </span>
            )}
            {/* Uploaded count (left) and total articles (right). */}
            <div className="flex items-baseline justify-between gap-2 text-xs text-muted-foreground">
              <span className="tabular-nums">
                {row.uploadedCount.toLocaleString()}/{row.notExcludedCount.toLocaleString()} uploaded
              </span>
              <span className="shrink-0 tabular-nums">
                {row.total.toLocaleString()} {row.total === 1 ? "article total" : "articles total"}
              </span>
            </div>
            {/* Bar track. The bar inside fills a fraction of the track
                proportional to this collection's count relative to the largest
                collection, so bar length reflects absolute volume. Segments
                within the bar are flex-weighted by their absolute counts. */}
            <div
              className="w-full overflow-hidden rounded-sm bg-muted"
              style={{ height: BAR_HEIGHT }}
              role="img"
              aria-label={`PR article status breakdown for ${row.label}`}
            >
              {row.total > 0 ? (
                <div
                  className="flex h-full overflow-hidden rounded-sm"
                  style={{ width: `${row.widthFraction * 100}%` }}
                >
                  {row.segments.map((segment) =>
                    segment.count > 0 ? (
                      <div
                        key={segment.category}
                        style={{ flexGrow: segment.count, backgroundColor: segment.fill }}
                        onMouseMove={(event) => handleSegmentHover(event, row, segment)}
                        onMouseLeave={() => setTooltip(null)}
                      />
                    ) : null
                  )}
                </div>
              ) : null}
            </div>
          </li>
        ))}
      </ul>

      {/* Hover tooltip — portalled to document.body with fixed positioning so
          it is never clipped or covered by the sidebar or ancestor overflow. */}
      {tooltip !== null &&
        createPortal(
          <div
            className="pointer-events-none fixed z-50 -translate-x-1/2 -translate-y-full rounded-md border bg-popover px-2 py-1 text-xs shadow-md"
            style={{ left: tooltip.left, top: tooltip.top - 8 }}
            role="tooltip"
          >
            <div className="flex items-center gap-1.5">
              <span
                className="inline-block size-2.5 shrink-0 rounded-sm"
                style={{ backgroundColor: tooltip.fill }}
                aria-hidden="true"
              />
              <span className="font-medium">{tooltip.categoryLabel}</span>
            </div>
            <div className="mt-0.5 text-muted-foreground">
              {tooltip.collectionLabel}
              {": "}
              {tooltip.count.toLocaleString()}{" "}
              {tooltip.count === 1 ? "article" : "articles"}
            </div>
          </div>,
          document.body
        )}
    </div>
  )
}
